// Janela de lançar ou corrigir UM apontamento — a mesma para a grade e para o
// calendário (23/09/2026).
//
// Dois jeitos de dizer o tempo, e a pessoa escolhe: "Início e fim" (o que o
// calendário e o cronômetro produzem) ou "Só a quantidade" (o "trabalhei 3 h"
// de quem anota no fim do dia, que continua valendo). Trocar para "Só a
// quantidade" num lançamento que tinha horário APAGA o horário — é o que a
// pessoa está dizendo.
//
// Validação só no Salvar (nada que mude o layout ao sair do campo: um aviso
// que empurra o botão faz o clique cair no lugar errado).
import { useEffect, useMemo, useState } from 'react';
import { Alert, App as AntApp, Button, Input, Modal, Popconfirm, Segmented, Select, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import AtividadeCampo, { type AtividadeTipo } from './AtividadeCampo';
import {
  api, chaveDestino, destinoDaChave, duracaoEntre, formatarHoras, horarioParaMin, parseEntradaHoras,
  parseHorario, formatarHorario, type Apontamento, type CentroCustoOpc, type ContratoOpc,
} from './api';
import { perguntaDeSoma } from './horasUtils';
import { montarOpcoesDestino } from './destinos';

const { Text } = Typography;

export type RascunhoApontamento = {
  id?: string;
  data: string;
  contrato_id?: string | null;
  centro_custo_codigo?: string | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  minutos?: number | null;
  atividade_tipo_id?: number | null;
  atividade_tipo_nome?: string | null;
  descricao?: string | null;
  origem?: string;
  rotulo_destino?: string | null;
};

/** Apontamento da semana → rascunho da janela de edição. */
export const rascunhoDe = (a: Apontamento): RascunhoApontamento => ({
  id: a.id, data: a.data, contrato_id: a.contrato_id, centro_custo_codigo: a.centro_custo_codigo,
  hora_inicio: a.hora_inicio || null, hora_fim: a.hora_fim || null, minutos: a.minutos,
  atividade_tipo_id: a.atividade_tipo_id ?? null, atividade_tipo_nome: a.atividade_tipo_nome ?? null,
  descricao: a.descricao, origem: a.origem,
  rotulo_destino: a.contrato_numero ? `${a.contrato_numero} — ${a.contrato_titulo || ''}` : a.centro_custo_nome,
});

const ORIGEM_ROTULO: Record<string, string> = {
  timer: 'cronômetro', diario_campo: 'diário de campo', clockify: 'Clockify', sugestao: 'sugestão', manual: 'digitado',
};

export default function ModalApontamento({
  aberto, inicial, contratos, ccs, catalogo, emailAlvo, hoje, horasDia, apontamentosDaSemana,
  cronometroNoDia, onFechar, onSalvo,
}: {
  aberto: boolean;
  inicial: RascunhoApontamento | null;
  contratos: ContratoOpc[];
  ccs: CentroCustoOpc[];
  catalogo: AtividadeTipo[];
  emailAlvo?: string;
  hoje: string;
  horasDia: number;
  apontamentosDaSemana: Apontamento[];
  /** Minutos do cronômetro ligado que caem no dia (0 se nenhum). */
  cronometroNoDia: (data: string) => number;
  onFechar: () => void;
  onSalvo: (aviso?: string) => void;
}) {
  const { modal } = AntApp.useApp();
  const editando = !!inicial?.id;
  const [destino, setDestino] = useState<string | undefined>();
  const [data, setData] = useState('');
  const [modo, setModo] = useState<'horario' | 'quantidade'>('horario');
  const [ini, setIni] = useState('');
  const [fim, setFim] = useState('');
  const [qtd, setQtd] = useState('');
  const [texto, setTexto] = useState('');
  const [atividade, setAtividade] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Semeia a cada abertura — nunca carrega o estado de uma edição anterior.
  useEffect(() => {
    if (!aberto || !inicial) return;
    setDestino(inicial.contrato_id || inicial.centro_custo_codigo
      ? chaveDestino({ contrato_id: inicial.contrato_id || null, centro_custo_codigo: inicial.centro_custo_codigo || null })
      : undefined);
    setData(inicial.data);
    // Abre no jeito em que o lançamento está. Novo: "Início e fim" quando veio
    // do calendário (já traz horário); da grade, "Só a quantidade", o hábito.
    setModo(inicial.hora_inicio ? 'horario' : 'quantidade');
    setIni(inicial.hora_inicio || '');
    setFim(inicial.hora_fim || '');
    setQtd(inicial.minutos ? formatarHoras(inicial.minutos) : '');
    setTexto(inicial.descricao || '');
    setAtividade(inicial.atividade_tipo_id ?? null);
    setErro(null);
  }, [aberto, inicial, editando]);

  // O item ligado pode ter saído da lista (desativado): continua aparecendo
  // no vínculo deste lançamento, com o nome que o servidor mandou.
  const catalogoComVinculo = useMemo(() => {
    if (!inicial?.atividade_tipo_id || catalogo.some(c => c.id === inicial.atividade_tipo_id)) return catalogo;
    return [...catalogo, { id: inicial.atividade_tipo_id, nome: inicial.atividade_tipo_nome || 'atividade', ativo: false }];
  }, [catalogo, inicial]);

  const opcoes = useMemo(() => montarOpcoesDestino(contratos, ccs, inicial), [contratos, ccs, inicial]);

  // Duração ao vivo, só para mostrar ("= 2h30"); a conta que vale é a do servidor.
  const previa = useMemo(() => {
    if (modo !== 'horario') return null;
    const a = parseHorario(ini); const b = parseHorario(fim, { permitir24: true });
    if (!('minutos' in a) || !('minutos' in b)) return null;
    const d = duracaoEntre(a.minutos, b.minutos);
    return 'minutos' in d ? formatarHoras(d.minutos) : null;
  }, [modo, ini, fim]);

  function montarCorpo(): { corpo: any; minutos: number } | { erro: string } {
    if (!destino) return { erro: 'Escolha onde você trabalhou.' };
    if (!data) return { erro: 'Escolha o dia.' };
    if (data > hoje) return { erro: 'Não dá para lançar hora num dia que ainda não chegou.' };
    const corpo: any = {
      ...destinoDaChave(destino), data,
      descricao: texto.trim() || null,
    };
    // A atividade só vai quando MUDOU (na correção) ou existe (no novo): um
    // `null` que não muda nada não tem por que viajar — e num banco ainda sem
    // a lista de atividades ele custaria a correção inteira.
    if (editando ? atividade !== (inicial?.atividade_tipo_id ?? null) : atividade !== null) {
      corpo.atividade_tipo_id = atividade;
    }
    let minutos = 0;
    if (modo === 'horario') {
      const a = parseHorario(ini); const b = parseHorario(fim, { permitir24: true });
      if ('vazio' in a || 'vazio' in b) return { erro: 'Diga de que horas a que horas — ou escolha "Só a quantidade".' };
      if ('erro' in a) return { erro: `Início: ${a.erro}` };
      if ('erro' in b) return { erro: `Fim: ${b.erro}` };
      const d = duracaoEntre(a.minutos, b.minutos);
      if ('erro' in d) return { erro: d.erro };
      corpo.hora_inicio = formatarHorario(a.minutos);
      corpo.hora_fim = formatarHorario(b.minutos);
      minutos = d.minutos;
    } else {
      const r = parseEntradaHoras(qtd);
      if ('erro' in r) return { erro: r.erro };
      if (!r.minutos) return { erro: 'Quantas horas?' };
      corpo.minutos = r.minutos;
      minutos = r.minutos;
      // Na edição, "Só a quantidade" diz ao servidor para APAGAR o horário.
      if (editando) { corpo.hora_inicio = null; corpo.hora_fim = null; }
    }
    if (!editando && emailAlvo) corpo.usuario_email = emailAlvo;
    return { corpo, minutos };
  }

  // "Trabalhei 8 h" no mesmo dia em que o cronômetro já marcou 3 h é o jeito
  // mais provável de contar a mesma hora duas vezes na troca de hábito. Não
  // recusa (pode ser verdade) — pergunta, com os números. A régua é a mesma
  // do atalho e da grade (perguntaDeSoma), e enxerga o cronômetro correndo.
  function avisoDeSoma(minutosNovos: number): string | null {
    const doDia = apontamentosDaSemana.filter(a => a.data === data && a.id !== inicial?.id)
      .map(a => ({ minutos: a.minutos, comHorario: !!a.hora_inicio, faixa: a.hora_inicio ? `${a.hora_inicio}–${a.hora_fim}` : null }));
    return perguntaDeSoma({
      data, novos: minutosNovos, novoComHorario: modo === 'horario', doDia,
      cronometroNoDia: cronometroNoDia(data), horasDia,
    });
  }

  async function gravar(corpo: any) {
    setSalvando(true); setErro(null);
    try {
      if (editando) await api(`/financeiro/horas/${inicial!.id}`, { method: 'PATCH', body: JSON.stringify(corpo) });
      else await api('/financeiro/horas', { method: 'POST', body: JSON.stringify(corpo) });
      onSalvo();
    } catch (e: any) { setErro(e.message); }
    finally { setSalvando(false); }
  }

  function salvar() {
    const m = montarCorpo();
    if ('erro' in m) { setErro(m.erro); return; }
    // Na correção, só pergunta se as horas aumentaram ou mudaram de dia —
    // trocar a descrição não é somar nada.
    const somou = !editando || m.minutos > (inicial?.minutos || 0) || data !== inicial?.data;
    const aviso = somou ? avisoDeSoma(m.minutos) : null;
    if (aviso) {
      modal.confirm({
        title: 'Somar estas horas?', content: aviso, okText: 'Somar', cancelText: 'Voltar e corrigir',
        onOk: () => gravar(m.corpo),
      });
      return;
    }
    gravar(m.corpo);
  }

  async function apagar() {
    if (!inicial?.id) return;
    setSalvando(true);
    try {
      await api(`/financeiro/horas/${inicial.id}`, { method: 'DELETE' });
      onSalvo('Lançamento apagado');
    } catch (e: any) { setErro(e.message); }
    finally { setSalvando(false); }
  }

  const rotulo = (t: string) => <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>{t}</Text>;

  return (
    <Modal
      open={aberto} onCancel={onFechar} destroyOnHidden
      title={editando ? 'Corrigir lançamento' : 'Lançar horas'}
      width="94vw" style={{ maxWidth: 640, top: 24 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <span>
            {editando && (
              <Popconfirm title="Apagar este lançamento?" okText="Apagar" cancelText="Não"
                okButtonProps={{ danger: true }} onConfirm={apagar}>
                <Button danger icon={<DeleteOutlined />} disabled={salvando}>Apagar</Button>
              </Popconfirm>
            )}
          </span>
          <Space>
            <Button onClick={onFechar}>Cancelar</Button>
            <Button type="primary" loading={salvando} onClick={salvar}>Salvar</Button>
          </Space>
        </div>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          {rotulo('Onde')}
          <Select
            showSearch style={{ width: '100%' }} placeholder="Contrato ou centro de custo"
            value={destino} onChange={setDestino} options={opcoes} optionFilterProp="label"
          />
        </div>
        <div>
          {rotulo('Dia')}
          <Input type="date" value={data} max={hoje} onChange={e => setData(e.target.value)} />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Segmented
          block value={modo} onChange={(v) => setModo(v as any)}
          options={[{ label: 'Início e fim', value: 'horario' }, { label: 'Só a quantidade', value: 'quantidade' }]}
        />
      </div>

      {modo === 'horario' ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 120 }}>
            {rotulo('Das')}
            <Input value={ini} onChange={e => setIni(e.target.value)} placeholder="08:30" inputMode="numeric" />
          </div>
          <div style={{ width: 120 }}>
            {rotulo('Às')}
            <Input value={fim} onChange={e => setFim(e.target.value)} placeholder="12:00" inputMode="numeric" />
          </div>
          <Text type="secondary" style={{ paddingBottom: 6 }}>{previa ? `= ${previa}` : ' '}</Text>
        </div>
      ) : (
        <div style={{ marginTop: 12, width: 180 }}>
          {rotulo('Quantas horas')}
          <Input value={qtd} onChange={e => setQtd(e.target.value)} placeholder="2 · 1,5 · 1:30" suffix="h" />
          {editando && horarioParaMin(inicial?.hora_inicio) !== null && (
            <Text type="secondary" style={{ fontSize: 12 }}>O horário {inicial?.hora_inicio}–{inicial?.hora_fim} será tirado.</Text>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {rotulo('O que você fez')}
        <AtividadeCampo
          texto={texto} atividadeId={atividade} catalogo={catalogoComVinculo}
          onTexto={setTexto} onAtividade={setAtividade}
        />
      </div>

      {editando && inicial?.origem && inicial.origem !== 'manual' && (
        <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
          Veio de: {ORIGEM_ROTULO[inicial.origem] || inicial.origem}
        </Text>
      )}

      {erro && <Alert type="error" showIcon style={{ marginTop: 14 }} message={erro} />}
    </Modal>
  );
}
